import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import analytics from './analytics';
import PremiumBirthDateTimePicker from './PremiumBirthDateTimePicker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Zodiac wheel SVG component
const ZodiacWheel = ({ sunSign, sunSignSymbol }) => {
  const signs = [
    { sign: 'aries', symbol: '♈', name: 'Koç', color: '#ef4444' },
    { sign: 'taurus', symbol: '♉', name: 'Boğa', color: '#22c55e' },
    { sign: 'gemini', symbol: '♊', name: 'İkizler', color: '#eab308' },
    { sign: 'cancer', symbol: '♋', name: 'Yengeç', color: '#3b82f6' },
    { sign: 'leo', symbol: '♌', name: 'Aslan', color: '#f97316' },
    { sign: 'virgo', symbol: '♍', name: 'Başak', color: '#84cc16' },
    { sign: 'libra', symbol: '♎', name: 'Terazi', color: '#ec4899' },
    { sign: 'scorpio', symbol: '♏', name: 'Akrep', color: '#dc2626' },
    { sign: 'sagittarius', symbol: '♐', name: 'Yay', color: '#8b5cf6' },
    { sign: 'capricorn', symbol: '♑', name: 'Oğlak', color: '#6b7280' },
    { sign: 'aquarius', symbol: '♒', name: 'Kova', color: '#06b6d4' },
    { sign: 'pisces', symbol: '♓', name: 'Balık', color: '#6366f1' },
  ];

  return (
    <div style={{ 
      position: 'relative', 
      width: '280px', 
      height: '280px', 
      margin: '0 auto 24px',
      background: 'radial-gradient(circle, rgba(139,92,246,0.15) 0%, transparent 70%)',
      borderRadius: '50%'
    }}>
      {/* Outer ring */}
      <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%' }}>
        <defs>
          <linearGradient id="wheelGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(139,92,246,0.3)" />
            <stop offset="100%" stopColor="rgba(59,130,246,0.3)" />
          </linearGradient>
        </defs>
        
        {/* Outer circle */}
        <circle cx="100" cy="100" r="95" fill="none" stroke="url(#wheelGradient)" strokeWidth="2" />
        <circle cx="100" cy="100" r="70" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
        <circle cx="100" cy="100" r="45" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
        
        {/* Zodiac segments */}
        {signs.map((s, i) => {
          const angle = (i * 30 - 90) * (Math.PI / 180);
          const x = 100 + 82 * Math.cos(angle);
          const y = 100 + 82 * Math.sin(angle);
          const isActive = s.sign === sunSign;
          
          return (
            <g key={s.sign}>
              {/* Segment line */}
              <line 
                x1="100" y1="100" 
                x2={100 + 95 * Math.cos(angle)} 
                y2={100 + 95 * Math.sin(angle)} 
                stroke="rgba(255,255,255,0.1)" 
                strokeWidth="1" 
              />
              {/* Symbol */}
              <text
                x={x}
                y={y}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={isActive ? s.color : 'rgba(255,255,255,0.4)'}
                fontSize={isActive ? '16' : '12'}
                fontWeight={isActive ? 'bold' : 'normal'}
                style={{ 
                  filter: isActive ? `drop-shadow(0 0 8px ${s.color})` : 'none',
                  transition: 'all 0.3s'
                }}
              >
                {s.symbol}
              </text>
            </g>
          );
        })}
        
        {/* Center sun symbol */}
        <circle cx="100" cy="100" r="25" fill="rgba(139,92,246,0.2)" stroke="rgba(139,92,246,0.5)" strokeWidth="2" />
        <text x="100" y="100" textAnchor="middle" dominantBaseline="middle" fill="#c084fc" fontSize="24">
          {sunSignSymbol || '☉'}
        </text>
      </svg>
      
      {/* Glow effect for active sign */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '60px',
        height: '60px',
        background: 'radial-gradient(circle, rgba(139,92,246,0.4) 0%, transparent 70%)',
        borderRadius: '50%',
        animation: 'pulse 2s ease-in-out infinite'
      }} />
    </div>
  );
};

// Astrology Summary Card
const AstroSummaryCard = ({ data }) => {
  const summaryItems = [
    { label: 'Güneş Burcu', value: `${data.sun_sign_name} ${data.sun_sign_symbol}`, icon: '☉', color: '#fbbf24' },
    { label: 'Element', value: data.element, icon: data.element === 'Ateş' ? '🔥' : data.element === 'Toprak' ? '🌍' : data.element === 'Hava' ? '💨' : '💧', color: '#60a5fa' },
    { label: 'Nitelik', value: data.quality, icon: '⚡', color: '#a78bfa' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: 'linear-gradient(135deg, rgba(139,92,246,0.15) 0%, rgba(59,130,246,0.1) 100%)',
        border: '1px solid rgba(139,92,246,0.3)',
        borderRadius: '16px',
        padding: '20px',
        marginBottom: '24px'
      }}
    >
      <h3 style={{ color: 'white', fontSize: '16px', marginBottom: '16px', textAlign: 'center' }}>
        ✨ Astrolojik Profil Özeti
      </h3>
      <div style={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: '12px' }}>
        {summaryItems.map((item, i) => (
          <div key={i} style={{ textAlign: 'center', minWidth: '80px' }}>
            <div style={{ fontSize: '24px', marginBottom: '4px' }}>{item.icon}</div>
            <div style={{ color: item.color, fontWeight: '600', fontSize: '14px' }}>{item.value}</div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>{item.label}</div>
          </div>
        ))}
      </div>
      <p style={{ 
        color: 'rgba(255,255,255,0.4)', 
        fontSize: '11px', 
        textAlign: 'center', 
        marginTop: '12px',
        fontStyle: 'italic'
      }}>
        * Güneş burcu doğum tarihinden hesaplanmıştır
      </p>
    </motion.div>
  );
};

const NatalChartTab = ({ user, onCreditUsed }) => {
  const [form, setForm] = useState({ 
    birth_date: '', 
    birth_time: '', 
    birth_city: '',
    unknown_time: false 
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('character');

  const creditCost = 8;
  // Elite/Pro users have unlimited credits
  const isUnlimited = user.subscription_type === 'elite' || user.subscription_type === 'pro' || user.credits === -1;
  const hasEnoughCredits = isUnlimited || (user.credits || 0) >= creditCost;

  // Premium categories - 7 main categories
  const categories = [
    { key: 'character', icon: '🪞', label: 'Karakter' },
    { key: 'love', icon: '💕', label: 'Aşk & İlişkiler' },
    { key: 'career', icon: '💼', label: 'Kariyer & Para' },
    { key: 'health', icon: '🌿', label: 'Sağlık' },
    { key: 'strengths', icon: '⚡', label: 'Güçlü Yönler' },
    { key: 'monthly_guide', icon: '📅', label: '30 Günlük Rehber' },
    { key: 'life_purpose', icon: '✨', label: 'Yaşam Amacı' },
  ];

  const handleAnalyze = async () => {
    if (!form.birth_date || !form.birth_city) return;
    analytics.natalChartCreated();
    setLoading(true);
    setResult(null);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API_URL}/api/natal-chart/reading`, {
        birth_date: form.birth_date,
        birth_time: form.unknown_time ? '' : form.birth_time,
        birth_city: form.birth_city,
        unknown_time: form.unknown_time,
        language: 'tr'
      }, { headers: { Authorization: `Bearer ${token}` } });
      setResult(data);
      if (onCreditUsed) onCreditUsed();
    } catch (err) {
      setResult({ error: err.response?.data?.detail || 'Bir hata oluştu' });
    } finally {
      setLoading(false);
    }
  };

  // Form view
  if (!result || result.error) {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="tab-content">
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <h2 style={{ color: 'white', marginBottom: '8px', fontSize: '24px' }}>
            🗺️ Premium Doğum Haritası
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>
            Kapsamlı astrolojik profil analizi • 11 detaylı bölüm • 30 günlük rehber
          </p>
        </div>

        {/* Premium features banner */}
        <div style={{
          background: 'linear-gradient(90deg, rgba(139,92,246,0.1), rgba(59,130,246,0.1))',
          border: '1px solid rgba(139,92,246,0.2)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center' }}>
            {['Karakter Analizi', 'Aşk & İlişkiler', 'Kariyer Rehberi', '30 Günlük Plan', 'Güçlü Yönler'].map((f, i) => (
              <span key={i} style={{
                background: 'rgba(139,92,246,0.2)',
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '11px',
                color: '#c084fc'
              }}>
                ✓ {f}
              </span>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
          <PremiumBirthDateTimePicker
            dateValue={form.birth_date}
            onDateChange={(date) => setForm(prev => ({ ...prev, birth_date: date }))}
            dateLabel="Doğum Tarihi"
            dateRequired={true}
            dateTestId="natal-birth-date"
            timeValue={form.birth_time}
            onTimeChange={(time) => setForm(prev => ({ ...prev, birth_time: time }))}
            timeLabel="Doğum Saati"
            timeTestId="natal-birth-time"
            unknownTime={form.unknown_time}
            onUnknownTimeChange={(val) => setForm(prev => ({ ...prev, unknown_time: val, birth_time: val ? '' : prev.birth_time }))}
            showUnknownTimeOption={true}
          />
          <div>
            <label style={{ display: 'block', color: 'rgba(255,255,255,0.6)', fontSize: '13px', marginBottom: '6px' }}>
              Doğum Şehri <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="text"
              placeholder="Örn: İstanbul, Ankara, İzmir..."
              value={form.birth_city}
              onChange={(e) => setForm(prev => ({ ...prev, birth_city: e.target.value }))}
              data-testid="natal-birth-city"
              style={{
                width: '100%', padding: '16px', boxSizing: 'border-box',
                background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: '12px', color: 'white', fontSize: '16px'
              }}
            />
          </div>
        </div>

        <button
          className="btn-primary btn-large"
          onClick={handleAnalyze}
          disabled={!form.birth_date || !form.birth_city || loading || !hasEnoughCredits}
          data-testid="natal-analyze-button"
          style={{ 
            width: '100%',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
            padding: '16px',
            fontSize: '16px'
          }}
        >
          {loading ? (
            <span>
              <span className="loading-spinner" style={{ marginRight: '8px' }}>⏳</span>
              Premium harita oluşturuluyor...
            </span>
          ) : (
            `Premium Doğum Haritamı Oluştur (${creditCost} 💎)`
          )}
        </button>

        {!hasEnoughCredits && (
          <div style={{ marginTop: '12px', padding: '12px', background: 'rgba(239,68,68,0.1)', borderRadius: '10px', textAlign: 'center', color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>
            Yetersiz kredi. Bu premium analiz {creditCost} kredi gerektirir.
          </div>
        )}

        {result?.error && (
          <div style={{ marginTop: '16px', padding: '14px', background: 'rgba(239,68,68,0.1)', borderRadius: '10px', color: '#f87171', textAlign: 'center' }}>
            {result.error}
          </div>
        )}
      </motion.div>
    );
  }

  // Result view - Premium
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="tab-content">
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <h2 style={{ color: 'white', marginBottom: '4px' }}>🗺️ Doğum Haritanız</h2>
        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
          {result.birth_city} • {result.birth_date} {result.birth_time && `• ${result.birth_time}`}
        </p>
      </div>

      {/* Zodiac Wheel */}
      <ZodiacWheel sunSign={result.sun_sign} sunSignSymbol={result.sun_sign_symbol} />

      {/* Astro Summary */}
      <AstroSummaryCard data={result} />

      {/* Category Navigation */}
      <div style={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: '6px', 
        marginBottom: '16px', 
        justifyContent: 'center',
        padding: '0 8px'
      }}>
        {categories.map((cat) => {
          const hasContent = result.categories?.[cat.key];
          return (
            <button
              key={cat.key}
              onClick={() => setActiveCategory(cat.key)}
              data-testid={`natal-category-${cat.key}`}
              disabled={!hasContent}
              style={{
                padding: '6px 12px',
                borderRadius: '16px',
                cursor: hasContent ? 'pointer' : 'not-allowed',
                fontSize: '12px',
                background: activeCategory === cat.key 
                  ? 'linear-gradient(135deg, rgba(139,92,246,0.4), rgba(99,102,241,0.4))'
                  : 'rgba(255,255,255,0.05)',
                border: activeCategory === cat.key 
                  ? '1px solid rgba(139,92,246,0.6)' 
                  : '1px solid rgba(255,255,255,0.1)',
                color: hasContent ? 'white' : 'rgba(255,255,255,0.3)',
                transition: 'all 0.2s',
                opacity: hasContent ? 1 : 0.5
              }}
            >
              {cat.icon} {cat.label}
            </button>
          );
        })}
      </div>

      {/* Content Display */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeCategory}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          style={{
            background: 'linear-gradient(135deg, rgba(139,92,246,0.08), rgba(59,130,246,0.05))',
            border: '1px solid rgba(139,92,246,0.2)',
            borderRadius: '16px',
            padding: '24px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
            <span style={{ fontSize: '24px', marginRight: '12px' }}>
              {categories.find(c => c.key === activeCategory)?.icon}
            </span>
            <h3 style={{ color: 'white', margin: 0, fontSize: '18px' }}>
              {categories.find(c => c.key === activeCategory)?.label}
            </h3>
          </div>
          <div 
            data-testid="natal-result" 
            style={{ 
              whiteSpace: 'pre-wrap', 
              lineHeight: 1.9, 
              color: 'rgba(255,255,255,0.85)', 
              fontSize: '15px' 
            }}
          >
            {result.categories?.[activeCategory] || 'Bu bölüm için içerik oluşturuluyor...'}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Back button */}
      <button
        onClick={() => setResult(null)}
        style={{
          marginTop: '20px',
          padding: '12px 24px',
          background: 'rgba(255,255,255,0.1)',
          border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: '10px',
          color: 'white',
          cursor: 'pointer',
          width: '100%'
        }}
      >
        ← Yeni Doğum Haritası Oluştur
      </button>
    </motion.div>
  );
};

export default NatalChartTab;
